import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { Layout } from '../../components/Layout.js';
import { PhoneInput } from '../../components/PhoneInput.js';
import { api, ApiClientError } from '../../lib/api.js';
import { trackBeginCheckout } from '../../lib/analytics.js';

interface IntentResponse {
  orderId: string;
  /** `null` para órdenes gratis (RSVP) — no hay PaymentIntent que confirmar. */
  clientSecret: string | null;
  publishableKey: string;
  subtotalCents: number;
  serviceFeeCents: number;
  /** Tarifa de servicio de Boletra que paga el comprador (encima del precio). */
  platformFeeCents: number;
  total: number;
  currency: string;
}

interface CheckoutEventResponse {
  event: { id: string };
  ticketTypes: Array<{ id: string; priceCents: number }>;
}

export function CheckoutPage() {
  const { orgSlug, eventSlug, lang } = useParams();
  const [searchParams] = useSearchParams();
  const itemsParam = searchParams.get('items') ?? '';
  const items = useMemo(() => decodeItems(itemsParam), [itemsParam]);
  const { t, i18n } = useTranslation('event');
  const navigate = useNavigate();
  const [buyerName, setBuyerName] = useState('');
  const [buyerEmail, setBuyerEmail] = useState('');
  const [buyerPhone, setBuyerPhone] = useState('');
  const [intent, setIntent] = useState<IntentResponse | null>(null);
  const [stripeInstance, setStripeInstance] = useState<Stripe | null>(null);

  // Misma queryKey que PublicEventPage: al venir desde el evento ya está en
  // caché. Lo necesitamos antes del submit para saber si el carrito es 100%
  // gratis (RSVP) y ajustar título/botón sin pasar por Stripe.
  const eventQuery = useQuery({
    queryKey: ['public-event', orgSlug, eventSlug, lang],
    queryFn: () =>
      api<CheckoutEventResponse>(`/events/by-slug/${orgSlug}/${eventSlug}?locale=${lang}`),
    enabled: Boolean(orgSlug && eventSlug),
  });

  const isFree = useMemo(() => {
    const tts = eventQuery.data?.ticketTypes;
    if (!tts || items.length === 0) return false;
    return items.every((it) => {
      const tt = tts.find((x) => x.id === it.ticketTypeId);
      return tt != null && tt.priceCents === 0;
    });
  }, [eventQuery.data, items]);

  const createIntent = useMutation({
    mutationFn: async () => {
      const eventId =
        eventQuery.data?.event.id ??
        (await api<CheckoutEventResponse>(`/events/by-slug/${orgSlug}/${eventSlug}?locale=${lang}`))
          .event.id;
      const utm = readUtm();
      return api<IntentResponse>('/checkout/intent', {
        method: 'POST',
        body: {
          eventId,
          buyer: {
            email: buyerEmail,
            name: buyerName,
            phone: buyerPhone || undefined,
            locale: i18n.language,
          },
          items,
          utm,
        },
      });
    },
    onSuccess: (res) => {
      trackBeginCheckout({
        eventId: res.orderId,
        eventName: eventSlug ?? 'event',
        currency: res.currency,
        valueCents: res.total,
        quantity: items.reduce((s, it) => s + it.quantity, 0),
      });
      // Orden gratis: el API ya emitió los tickets, vamos directo a la
      // confirmación — no hay PaymentIntent que confirmar.
      if (!res.clientSecret) {
        navigate(`/${lang}/orders/${res.orderId}`);
        return;
      }
      setIntent(res);
      // Importante: este es un destination charge — el PaymentIntent vive en
      // la cuenta plataforma, NO en la Connect de la productora. Pasar
      // `stripeAccount` haría que Stripe.js buscara el PI en la cuenta
      // equivocada y el PaymentElement nunca montaría.
      loadStripe(res.publishableKey).then(setStripeInstance);
    },
  });

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-bold mb-8">
          {isFree ? t('checkout.title_free') : t('checkout.title')}
        </h1>
        {!intent && (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              createIntent.mutate();
            }}
          >
            <label className="block">
              <span className="text-sm text-neutral-400">{t('checkout.buyer_name')}</span>
              <input
                required
                value={buyerName}
                onChange={(e) => setBuyerName(e.target.value)}
                className="input mt-2"
              />
            </label>
            <label className="block">
              <span className="text-sm text-neutral-400">{t('checkout.buyer_email')}</span>
              <input
                required
                type="email"
                value={buyerEmail}
                onChange={(e) => setBuyerEmail(e.target.value)}
                className="input mt-2"
              />
            </label>
            <label className="block">
              <span className="text-sm text-neutral-400">{t('checkout.buyer_phone')}</span>
              <div className="mt-2">
                <PhoneInput value={buyerPhone} onChange={setBuyerPhone} />
              </div>
            </label>
            <button type="submit" disabled={createIntent.isPending} className="btn-primary w-full">
              {createIntent.isPending
                ? isFree
                  ? t('checkout.processing_free')
                  : t('checkout.processing')
                : isFree
                  ? t('checkout.continue')
                  : t('checkout.continue_to_payment')}
            </button>
            {createIntent.isError &&
              (() => {
                const err = createIntent.error as ApiClientError;
                const message =
                  err.code === 'credits.insufficient' || err.code === 'credits.balance_zero'
                    ? t('checkout.errors.unavailable')
                    : err.message;
                return <p className="text-red-400 text-sm">{message}</p>;
              })()}
          </form>
        )}
        {intent && intent.clientSecret && stripeInstance && (
          <Elements
            stripe={stripeInstance}
            options={{ clientSecret: intent.clientSecret, appearance: { theme: 'night' } }}
          >
            <PaymentForm intent={intent} buyerEmail={buyerEmail} />
          </Elements>
        )}
      </div>
    </Layout>
  );
}

function PaymentForm({ intent, buyerEmail }: { intent: IntentResponse; buyerEmail: string }) {
  const stripe = useStripe();
  const elements = useElements();
  const { t, i18n } = useTranslation('event');
  const navigate = useNavigate();
  const { lang } = useParams();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
  }, []);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/${lang}/orders/${intent.orderId}`,
        receipt_email: buyerEmail,
      },
    });
    if (error) {
      setError(error.message ?? t('checkout.errors.stripe_unavailable'));
      setSubmitting(false);
    } else {
      // Redirected by Stripe; fallback in case it doesn't:
      navigate(`/${lang}/orders/${intent.orderId}`);
    }
  };

  const fmt = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: intent.currency,
  });

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="card space-y-2 text-sm">
        <div className="flex justify-between text-neutral-400">
          <span>{t('checkout.breakdown_subtotal')}</span>
          <span className="tabular-nums">{fmt.format(intent.subtotalCents / 100)}</span>
        </div>
        {intent.serviceFeeCents > 0 && (
          <div className="flex justify-between text-neutral-400">
            <span>{t('checkout.breakdown_service_fee')}</span>
            <span className="tabular-nums">{fmt.format(intent.serviceFeeCents / 100)}</span>
          </div>
        )}
        {intent.platformFeeCents > 0 && (
          <div className="flex justify-between text-neutral-400">
            <span>{t('checkout.breakdown_boletra_fee')}</span>
            <span className="tabular-nums">{fmt.format(intent.platformFeeCents / 100)}</span>
          </div>
        )}
        <div className="flex justify-between font-semibold text-neutral-100 pt-2 border-t border-neutral-800">
          <span>{t('checkout.breakdown_total')}</span>
          <span className="tabular-nums">{fmt.format(intent.total / 100)}</span>
        </div>
      </div>
      <PaymentElement />
      <button type="submit" disabled={submitting || !stripe} className="btn-primary w-full">
        {submitting
          ? t('checkout.processing')
          : t('checkout.confirm_payment', { amount: fmt.format(intent.total / 100) })}
      </button>
      {error && <p className="text-red-400 text-sm">{error}</p>}
      <p className="text-xs text-neutral-500 text-center">
        {fmt.format(intent.total / 100)} · Powered by Stripe
      </p>
    </form>
  );
}

function decodeItems(raw: string): Array<{ ticketTypeId: string; quantity: number }> {
  if (!raw) return [];
  const parsed: Array<{ ticketTypeId: string; quantity: number }> = [];
  for (const entry of raw.split(',')) {
    const [id, qty] = entry.split(':');
    if (!id || !qty) continue;
    const quantity = Number(qty);
    if (!Number.isFinite(quantity) || quantity <= 0) continue;
    parsed.push({ ticketTypeId: id, quantity });
  }
  return parsed;
}

function readUtm() {
  const params = new URLSearchParams(window.location.search);
  const out: Record<string, string> = {};
  for (const key of ['source', 'medium', 'campaign', 'term', 'content']) {
    const v = params.get(`utm_${key}`);
    if (v) out[key] = v;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}
